import {
  useEventOccurrenceDelete,
  useMutationEventOccurrenceCheckInAll,
} from "@gymflow/api";
import { pluralize } from "@gymflow/helpers";
import { EventRsvpDTO } from "@gymflow/types";
import classNames from "classnames";
import { useContext } from "react";
import { useHistory } from "react-router";

import { ModalContext, useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import { Button, PrimaryButton, TextButton } from "../../atoms";
import { SendEmailSidebarProviderContext } from "../../organisms";
import { ConfirmModal } from "../../templates";
import { SideBarOccurrenceFormProviderContext } from "../SideBarOccurrence/SideBarOccurrenceFormProvider";
import { RecurrentCancelModal } from "./RecurrentCancelModal";

export function EventOccurrenceButtons({
  occurrenceId,
  rsvpList = [],
  isBookable,
  isRecurring,
}: {
  occurrenceId?: number;
  rsvpList: EventRsvpDTO[];
  isBookable: boolean;
  isRecurring: boolean;
}) {
  const { openEdit } = useContext(SideBarOccurrenceFormProviderContext);

  const recipients = rsvpList
    .filter(({ status }) => status === "BOOKED" || status === "ATTENDED")
    .map(({ userMember }) => ({
      userMemberId: userMember.id,
    }));

  const { api } = useGymflowModels();

  const { mutateAsync: mutateAsyncEventOccurrenceCheckInAll } =
    useMutationEventOccurrenceCheckInAll({ api });

  const { club } = useClubSettings();

  const { openSendEmailSidebar } = useContext(SendEmailSidebarProviderContext);
  const cancelOccurrence = useEventOccurrenceDelete({ api });

  const { setModal, hide: hideModal } = useContext(ModalContext);
  const history = useHistory();

  return (
    <div className="flex flex-row-reverse gap-x-3 border border-gray-200 p-4">
      <PrimaryButton
        className="mt-0"
        disabled={!occurrenceId}
        onClick={() => {
          if (occurrenceId) {
            openEdit(occurrenceId);
          }
        }}
      >
        Edit Class
      </PrimaryButton>
      <Button
        className={classNames({ hidden: !isBookable })}
        disabled={!rsvpList.some((rsvp) => rsvp.status === "BOOKED")}
        onClick={async () => {
          if (!occurrenceId) {
            return;
          }

          await mutateAsyncEventOccurrenceCheckInAll({ occurrenceId });
        }}
      >
        Check In All
      </Button>
      <Button
        disabled={!club || !occurrenceId || recipients.length === 0}
        onClick={() => {
          if (club) {
            const to = `${recipients.length} ${pluralize(
              "User",
              "Users",
              recipients.length,
            )}`;
            openSendEmailSidebar({
              recipient: to,
              allowMarketing: true,
              requestType: "USER",
              listOfIds: recipients.map(({ userMemberId }) => userMemberId),
            });
          }
        }}
      >
        Email All
      </Button>
      <TextButton
        disabled={!occurrenceId}
        className="!text-error-700"
        onClick={() => {
          if (isRecurring) {
            setModal(
              <RecurrentCancelModal
                onConfirm={async (cancelAll) => {
                  await cancelOccurrence.mutateAsync({
                    occurrenceId: occurrenceId!,
                    isDeleteAll: cancelAll,
                  });
                  hideModal();
                  history.goBack();
                }}
                onCancel={() => {
                  hideModal();
                }}
              />,
            );
          } else {
            setModal(
              <ConfirmModal
                title="Cancel confirmation"
                onConfirm={async () => {
                  await cancelOccurrence.mutateAsync({
                    occurrenceId: occurrenceId!,
                    isDeleteAll: false,
                  });
                  history.goBack();
                  hideModal();
                }}
                onCancel={() => {
                  hideModal();
                }}
              >
                Are you sure you want to cancel this event?
              </ConfirmModal>,
            );
          }
        }}
      >
        Cancel Class
      </TextButton>
    </div>
  );
}
